import { Search, Sparkles, X } from "lucide-react"
import {
  type CSSProperties,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react"
import { createPortal } from "react-dom"
import { usePortalTarget } from "../cards/PortalTarget"
import { useI18n } from "../i18n/useI18n"
import type {
  TableFilterSuggestion,
  UseTableFilterResult,
} from "./tableFilters"

type ManagementFilterBarProps<RecordType> = {
  filter: UseTableFilterResult<RecordType>
  placeholder?: string
}

export function ManagementFilterBar<RecordType>({
  filter,
  placeholder,
}: ManagementFilterBarProps<RecordType>) {
  const { t } = useI18n()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const inputShellRef = useRef<HTMLLabelElement | null>(null)
  const [isFocused, setIsFocused] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const [suggestionsStyle, setSuggestionsStyle] =
    useState<CSSProperties | null>(null)
  const portalTarget = usePortalTarget()

  const suggestions = isFocused ? filter.suggestions : []
  const hasSuggestions = suggestions.length > 0

  useLayoutEffect(() => {
    if (!hasSuggestions || !inputShellRef.current) {
      setSuggestionsStyle(null)
      return
    }

    const updateSuggestionsPosition = () => {
      const rect = inputShellRef.current?.getBoundingClientRect()
      if (!rect) return

      const viewportPadding = 8
      const preferredWidth = Math.max(rect.width, 320)
      const maxWidth = Math.max(240, window.innerWidth - viewportPadding * 2)
      const width = Math.min(preferredWidth, maxWidth)
      const left = Math.min(
        Math.max(viewportPadding, rect.left),
        window.innerWidth - width - viewportPadding,
      )
      const top = rect.bottom + 6
      const maxHeight = Math.max(
        120,
        window.innerHeight - top - viewportPadding,
      )

      setSuggestionsStyle({
        left,
        top,
        width,
        maxHeight,
      })
    }

    updateSuggestionsPosition()

    const resizeObserver = new ResizeObserver(updateSuggestionsPosition)
    resizeObserver.observe(inputShellRef.current)

    window.addEventListener("resize", updateSuggestionsPosition)
    window.addEventListener("scroll", updateSuggestionsPosition, true)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener("resize", updateSuggestionsPosition)
      window.removeEventListener("scroll", updateSuggestionsPosition, true)
    }
  }, [hasSuggestions])

  useEffect(() => {
    if (!filter.hasPendingClickQuery) {
      return
    }

    const handleWindowKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
        event.preventDefault()
        filter.applyPendingQuery()
      }
    }

    window.addEventListener("keydown", handleWindowKeyDown)
    return () => {
      window.removeEventListener("keydown", handleWindowKeyDown)
    }
  }, [filter])

  const acceptSuggestion = (suggestion: TableFilterSuggestion) => {
    filter.updateDraftQuery(suggestion.nextQuery, suggestion.nextCursor)
    inputRef.current?.focus()
  }

  return (
    <div className="management-filter" ref={containerRef}>
      <div className="management-filter-input-row">
        <label className="management-filter-input-shell" ref={inputShellRef}>
          <Search aria-hidden="true" size={16} strokeWidth={2} />
          <input
            aria-label={t("filter.aria.input")}
            className="management-filter-input"
            onBlur={(event) => {
              const nextTarget = event.relatedTarget
              if (
                nextTarget instanceof Node &&
                containerRef.current?.contains(nextTarget)
              ) {
                return
              }
              setIsFocused(false)
            }}
            onChange={(event) => {
              setHighlightedIndex(0)
              filter.updateDraftQuery(
                event.target.value,
                event.target.selectionStart ?? event.target.value.length,
              )
            }}
            onClick={(event) => {
              setIsFocused(true)
              setHighlightedIndex(0)
              filter.setCursorPosition(event.currentTarget.selectionStart)
            }}
            onFocus={() => {
              setIsFocused(true)
              setHighlightedIndex(0)
            }}
            onKeyDown={(event) => {
              if (event.key === "Escape" && filter.hasPendingClickQuery) {
                event.preventDefault()
                filter.cancelPendingQuery()
                return
              }

              if (!hasSuggestions) {
                if (event.key === "Enter" && filter.hasPendingClickQuery) {
                  event.preventDefault()
                  filter.applyPendingQuery()
                }
                return
              }

              if (event.key === "ArrowDown") {
                event.preventDefault()
                setHighlightedIndex((current) =>
                  current + 1 >= suggestions.length ? 0 : current + 1,
                )
                return
              }

              if (event.key === "ArrowUp") {
                event.preventDefault()
                setHighlightedIndex((current) =>
                  current === 0 ? suggestions.length - 1 : current - 1,
                )
                return
              }

              if (
                (event.key === "Enter" || event.key === "Tab") &&
                suggestions[highlightedIndex]
              ) {
                event.preventDefault()
                acceptSuggestion(suggestions[highlightedIndex])
              }
            }}
            onKeyUp={(event) =>
              filter.setCursorPosition(event.currentTarget.selectionStart)
            }
            onSelect={(event) =>
              filter.setCursorPosition(event.currentTarget.selectionStart)
            }
            placeholder={placeholder ?? t("filter.placeholder.generic")}
            ref={inputRef}
            type="text"
            value={filter.draftQuery}
          />
          {filter.draftQuery ? (
            <button
              aria-label={t("filter.aria.clear")}
              className="management-filter-clear"
              onClick={() => filter.clearQuery()}
              type="button"
            >
              <X size={14} strokeWidth={2} />
            </button>
          ) : null}
        </label>

        {filter.hasPendingClickQuery ? (
          <div className="management-filter-actions">
            <button
              className="secondary-button"
              onClick={() => filter.applyPendingQuery()}
              type="button"
            >
              {t("filter.actions.apply")}
            </button>
            <button
              className="ghost-icon-button"
              onClick={() => filter.cancelPendingQuery()}
              type="button"
            >
              {t("filter.actions.cancel")}
            </button>
          </div>
        ) : null}
      </div>

      {hasSuggestions && suggestionsStyle
        ? createPortal(
            <div
              aria-label={t("filter.aria.suggestions")}
              className="management-filter-suggestions"
              role="listbox"
              style={suggestionsStyle}
            >
              {suggestions.map((suggestion, index) => (
                <button
                  aria-selected={highlightedIndex === index}
                  className="management-filter-suggestion"
                  data-highlighted={highlightedIndex === index}
                  key={suggestion.id}
                  onMouseDown={(event) => {
                    event.preventDefault()
                    acceptSuggestion(suggestion)
                  }}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  role="option"
                  type="button"
                >
                  <span className="management-filter-suggestion-main">
                    {suggestion.kind === "value" ? (
                      <Sparkles aria-hidden="true" size={14} strokeWidth={2} />
                    ) : null}
                    <span>{suggestion.label}</span>
                  </span>
                  {suggestion.detail ? (
                    <span className="management-filter-suggestion-detail">
                      {suggestion.detail}
                    </span>
                  ) : null}
                </button>
              ))}
            </div>,
            portalTarget,
          )
        : null}
    </div>
  )
}
